// App.js
// Main app shell for Virtual Hardhat
// Refactored in Phase 4 - Routes extracted to modular files

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, useLocation } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';

import { AuthProviderUnified } from './auth/AuthProviderUnified';
import ProcoreOAuthCallbackToast from './components/ProcoreOAuthCallbackToast';
import BoltFab from './components/BoltFab';
import CompatBanner from './components/CompatBanner';
import ContactSupportModal from './components/ContactSupportModal';
import DarkSuspenseFallback from './components/DarkSuspenseFallback';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalAIAssistant from './components/GlobalAIAssistant';
import PrivacyBanner from './components/PrivacyBanner';
import ProactiveAgent from './components/ProactiveAgent';
import GlobalDataStatusBanners from './components/common/GlobalDataStatusBanners';
import { ToastProvider } from './components/common/ToastContainer';
import { AgentStateProvider } from './contexts/AgentStateProvider';
import { AIAgentProvider } from './contexts/AIAgentContext';
import { ScheduleProvider } from './hooks/ScheduleProvider';
import { ThemeProvider } from './ThemeContext.jsx';
import usePMNotifications from './hooks/usePMNotifications';
import AppShell from './layouts/AppShell';

// Import route configuration functions from modular files
import { getProductionRoutes, get404Route } from './routes/routeConfig';
// Demo routes removed - demo functionality moved to vh-demo-web repository

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to prevent scroll restoration on route changes
function ScrollRestoration() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Disable browser scroll restoration completely
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Only scroll to top when navigating to a different page (not landing page)
    // And only if user explicitly navigated (not from modal/button interactions)
    if (pathname !== '/') {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
    // For landing page, maintain scroll position - never scroll to top
  }, [pathname]);

  return null;
}

function AppRoutes() {
  usePMNotifications();

  return (
    <ThemeProvider>
      {/* Global a11y gate: framer reduces transform/scale/layout motion (keeps
          opacity) for users with OS "Reduce Motion" enabled. */}
      <MotionConfig reducedMotion="user">
      <GlobalDataStatusBanners />
      <CompatBanner />
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        // Disable automatic scroll restoration to prevent scroll-to-top issues
      >
        <ScrollRestoration />
        <ProcoreOAuthCallbackToast />
        <AppShell>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <Routes>
              {getProductionRoutes()}
              {get404Route()}
            </Routes>
          </Suspense>
        </AppShell>
        <ContactSupportModal onClose={() => {}} />
        <PrivacyBanner />
        <GlobalAIAssistant />
        <ProactiveAgent />
        <BoltFab />
      </BrowserRouter>
      </MotionConfig>
    </ThemeProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProviderUnified>
        <ToastProvider>
        <AIAgentProvider>
          <ScheduleProvider>
            <AgentStateProvider>
              <ErrorBoundary
                errorTitle="Application Error"
                errorMessage="We encountered an unexpected error. Please refresh the page or contact support if the problem persists."
              >
                <AppRoutes />
              </ErrorBoundary>
            </AgentStateProvider>
          </ScheduleProvider>
        </AIAgentProvider>
        </ToastProvider>
      </AuthProviderUnified>
    </QueryClientProvider>
  );
}

export default App;
